import React, { memo } from "react";

import { classNames } from "helpers";
import { ShowcaseBackdrop, ShowcaseStage } from "ui/components/Showcase";
import Logo from "ui/modules/public/components/Logo/Logo";

import {
  WAREHOUSE_ADDRESS_STEP,
  WAREHOUSE_CONTACTS_STEP,
  WAREHOUSE_INFO_STEP,
  WAREHOUSE_INTRO_TEXTS,
  WAREHOUSE_WORKING_HOURS_STEP,
} from "./consts";
import WarehouseAddressScene from "./scenes/WarehouseAddressScene";
import WarehouseContactsScene from "./scenes/WarehouseContactsScene";
import WarehouseInfoScene from "./scenes/WarehouseInfoScene";
import WarehouseWorkingHoursScene from "./scenes/WarehouseWorkingHoursScene";
import { WarehouseIntroProps, WarehouseSceneProps } from "./types";

/** Scena za korak čarobnjaka skladišta. */
const STEP_SCENES: Record<number, React.FC<WarehouseSceneProps>> = {
  [WAREHOUSE_INFO_STEP]: WarehouseInfoScene,
  [WAREHOUSE_ADDRESS_STEP]: WarehouseAddressScene,
  [WAREHOUSE_WORKING_HOURS_STEP]: WarehouseWorkingHoursScene,
  [WAREHOUSE_CONTACTS_STEP]: WarehouseContactsScene,
};

/**
 * Lijevi panel unosa skladišta: za korak vlastita scena s karticama koje prikazuju upisane
 * podatke, a ispod objašnjenje koraka. Isti obrazac kao intro čarobnjaka vozila.
 */
const WarehouseIntro: React.FC<WarehouseIntroProps> = ({ formValues, stepCurrent, stepCount }) => {
  const stepKey = STEP_SCENES[stepCurrent] ? stepCurrent : WAREHOUSE_INFO_STEP;
  const Scene = STEP_SCENES[stepKey];
  const text = WAREHOUSE_INTRO_TEXTS[stepKey];

  return (
    <div className="relative flex flex-col w-full h-full bg-blue-700 overflow-hidden">
      <ShowcaseBackdrop />
      <Logo variant="light" animated className="absolute top-8 left-8 z-10" />

      <div className="relative flex flex-1 min-h-0 items-center justify-center select-none pointer-events-none" aria-hidden="true">
        {/* `key`: novi korak iznova montira scenu, pa kartice ponovno ulete. */}
        <ShowcaseStage key={stepKey}>
          <Scene formValues={formValues} />
        </ShowcaseStage>
      </div>

      <div className="relative px-8 pb-12 text-center">
        <div className="text-xs font-semibold uppercase tracking-widest text-blue-200">
          Korak {stepKey + 1} od {stepCount}
        </div>
        <div key={stepKey} className="motion-safe:animate-showcaseFadeIn">
          <div className="mt-2 text-3xl font-bold leading-tight text-white">{text.title}</div>
          {/* Visina dva retka: opisi su različite duljine, a scena iznad ne smije skakati. */}
          <p className="mt-3 mx-auto max-w-md min-h-[3rem] text-base text-blue-100">{text.description}</p>
        </div>
        <div className="flex items-center justify-center gap-2 mt-6">
          {Array.from({ length: stepCount }, (_, index) => (
            <span
              key={index}
              className={classNames(
                "h-1.5 rounded-full transition-all duration-300",
                index === stepKey ? "w-6 bg-amber-400" : "w-1.5 bg-white/40"
              )}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default memo(WarehouseIntro);
